// Minimal hash-based history sync — no router library. Route map (current):
//   #/today   #/board   #/my_requests   #/history
//   #/overview/<sub>   #/approvals   #/directory  #/directory/person/<id>
//   #/continuity/<sub>   #/sites/<sub>   #/leave/<sub>   #/security/<sub>
//   #/handbook/<sub>   #/remote   #/alerts
// where <sub> is one of each tab's VALID_*_SUBS below.
//
// The sidenav groups these into SELF / HR / ADMIN (see Shell.svelte). Every
// sub is URL-addressable and rendered as its own indented nav item — there
// is no in-page tab bar left anywhere in the app, so no view is reachable
// only by clicking through a page.
//
// Legacy redirects (see parseHash) carry every hash from the previous flat
// "dashboard/hr/manage" IA (and the redirects that IA itself inherited) onto
// its new home, so old bookmarks/shared links/stored notification routes
// keep working:
//   #/dashboard -> today                #/work_logs -> history
//   #/requests -> my_requests           #/tasks -> board (pre-existing)
//   #/holidays -> handbook/holidays     #/announcements -> handbook/announcements
//   #/rules -> handbook/rules           #/policy -> handbook/policy
//   #/admin(/login_events) -> security/logins (pre-existing chain)
//   #/hr/overview -> overview/pulse     #/hr/requests, #/hr/inbox -> approvals
//   #/hr/stats -> overview/stats        #/hr/employee/<id> -> directory/person/<id>
//   #/hr/{users|sites|assignments|devices} -> directory or sites/<same> (pre-existing chain)
//   #/manage/users -> directory         #/manage/{sites|assignments|devices} -> sites/<same>
//   #/manage/login_events -> security/logins   #/manage/audit -> security/audit
//   #/manage/leave_types -> leave/types  #/manage/housekeeping -> leave/housekeeping
//
// User-triggered navigation calls `push` (one history entry per view
// change); programmatic redirects (post-login landing, logout, bouncing off
// an invalid/unauthorized route) call `replace` so Back can't get trapped.

export type TabId =
  | 'today'
  | 'board'
  | 'team_board'
  | 'my_requests'
  | 'history'
  | 'overview'
  | 'approvals'
  | 'directory'
  | 'continuity'
  | 'sites'
  | 'leave'
  | 'security'
  | 'handbook'
  | 'remote'
  | 'alerts'
  | 'field';

export type OverviewSub = 'pulse' | 'sites' | 'stats' | 'flags';
export type SitesSub = 'sites' | 'assignments' | 'devices';
export type LeaveSub = 'types' | 'holidays' | 'housekeeping';
export type SecuritySub = 'logins' | 'audit' | 'api-keys';
export type HandbookSub = 'announcements' | 'rules' | 'policy' | 'holidays';
// Continuity's sub-nav is role-split (formerly ContinuityShell.svelte's
// job, now App.svelte's): admin gets risk/okrs/conversations/playbook,
// HR gets the restricted my_actions/conversations_due/trends. Both live in
// one union (like ManageSub used to span HR+admin) — App.svelte renders the
// role-correct set and fixes up a cross-role sub in normalizeRouteAfterAuth.
export type ContinuitySub = 'risk' | 'okrs' | 'conversations' | 'playbook' | 'my_actions' | 'conversations_due' | 'trends';

const VALID_OVERVIEW_SUBS: readonly OverviewSub[] = ['pulse', 'sites', 'stats', 'flags'];
const VALID_SITES_SUBS: readonly SitesSub[] = ['sites', 'assignments', 'devices'];
const VALID_LEAVE_SUBS: readonly LeaveSub[] = ['types', 'holidays', 'housekeeping'];
const VALID_SECURITY_SUBS: readonly SecuritySub[] = ['logins', 'audit', 'api-keys'];
const VALID_HANDBOOK_SUBS: readonly HandbookSub[] = ['announcements', 'rules', 'policy', 'holidays'];
const VALID_CONTINUITY_SUBS: readonly ContinuitySub[] = [
  'risk',
  'okrs',
  'conversations',
  'playbook',
  'my_actions',
  'conversations_due',
  'trends'
];
export const ADMIN_CONTINUITY_SUBS: readonly ContinuitySub[] = ['risk', 'okrs', 'conversations', 'playbook'];
export const HR_CONTINUITY_SUBS: readonly ContinuitySub[] = ['my_actions', 'conversations_due', 'trends'];

export const DEFAULT_OVERVIEW_SUB: OverviewSub = 'pulse';
export const DEFAULT_SITES_SUB: SitesSub = 'sites';
export const DEFAULT_LEAVE_SUB: LeaveSub = 'types';
export const DEFAULT_SECURITY_SUB: SecuritySub = 'logins';
export const DEFAULT_HANDBOOK_SUB: HandbookSub = 'announcements';
export const DEFAULT_ADMIN_CONTINUITY_SUB: ContinuitySub = 'risk';
export const DEFAULT_HR_CONTINUITY_SUB: ContinuitySub = 'my_actions';

export type Route =
  | { tab: 'today' | 'team_board' | 'my_requests' | 'history' | 'approvals' | 'remote' | 'alerts' | 'field' }
  | { tab: 'board'; sub?: 'card' | 'cos'; cardId?: number; update?: boolean; checkin?: boolean }
  | { tab: 'overview'; sub: OverviewSub }
  | { tab: 'directory'; sub?: undefined }
  | { tab: 'directory'; sub: 'person'; userId: number }
  | { tab: 'continuity'; sub: ContinuitySub }
  | { tab: 'sites'; sub: SitesSub }
  | { tab: 'leave'; sub: LeaveSub }
  | { tab: 'security'; sub: SecuritySub }
  | { tab: 'handbook'; sub: HandbookSub };

function toHash(r: Route): string {
  switch (r.tab) {
    case 'board':
      return r.sub === 'card' && r.cardId ? `#/board/card/${r.cardId}${r.update ? '?update=1' : ''}` : r.sub === 'cos' ? '#/board/cos' : `#/board${r.checkin ? '?checkin=1' : ''}`;
    case 'overview':
      return `#/overview/${r.sub}`;
    case 'directory':
      return r.sub === 'person' ? `#/directory/person/${r.userId}` : '#/directory';
    case 'continuity':
      return `#/continuity/${r.sub}`;
    case 'sites':
      return `#/sites/${r.sub}`;
    case 'leave':
      return `#/leave/${r.sub}`;
    case 'security':
      return `#/security/${r.sub}`;
    case 'handbook':
      return `#/handbook/${r.sub}`;
    default:
      return `#/${r.tab}`;
  }
}

function parseHash(hash: string): Route {
  const raw = hash.replace(/^\/?#?\/?/, '');
  const [pathname, search = ''] = raw.split('?');
  const params = new URLSearchParams(search);
  const [seg0, seg1, seg2] = pathname.split('/').filter(Boolean);

  // ---- legacy: renamed bare tabs ----
  if (seg0 === 'dashboard') return { tab: 'today' };
  if (seg0 === 'work_logs') return { tab: 'history' };
  if (seg0 === 'requests') return { tab: 'my_requests' };
  if (seg0 === 'tasks') return { tab: 'board' }; // pre-existing: old task-template list
  if (seg0 === 'holidays') return { tab: 'handbook', sub: 'holidays' };
  if (seg0 === 'announcements') return { tab: 'handbook', sub: 'announcements' };
  if (seg0 === 'rules') return { tab: 'handbook', sub: 'rules' };
  if (seg0 === 'policy') return { tab: 'handbook', sub: 'policy' };

  // ---- legacy: bare #/admin (pre-existing chain, historically -> manage/login_events) ----
  if (seg0 === 'admin') return { tab: 'security', sub: 'logins' };

  // ---- legacy: #/hr/* ----
  if (seg0 === 'hr') {
    if (seg1 === 'employee') {
      const userId = Number(seg2);
      if (Number.isInteger(userId) && userId > 0) return { tab: 'directory', sub: 'person', userId };
      return { tab: 'directory' };
    }
    if (seg1 === 'requests' || seg1 === 'inbox') return { tab: 'approvals' };
    if (seg1 === 'stats') return { tab: 'overview', sub: 'stats' };
    // pre-existing chain: old #/hr/{users|sites|assignments|devices} -> #/manage/<same>
    if (seg1 === 'users') return { tab: 'directory' };
    if (seg1 === 'sites') return { tab: 'sites', sub: 'sites' };
    if (seg1 === 'assignments') return { tab: 'sites', sub: 'assignments' };
    if (seg1 === 'devices') return { tab: 'sites', sub: 'devices' };
    return { tab: 'overview', sub: DEFAULT_OVERVIEW_SUB }; // bare #/hr or #/hr/overview
  }

  // ---- legacy: #/manage/* ----
  if (seg0 === 'manage') {
    if (seg1 === 'users') return { tab: 'directory' };
    if (seg1 === 'sites') return { tab: 'sites', sub: 'sites' };
    if (seg1 === 'assignments') return { tab: 'sites', sub: 'assignments' };
    if (seg1 === 'devices') return { tab: 'sites', sub: 'devices' };
    if (seg1 === 'login_events') return { tab: 'security', sub: 'logins' };
    if (seg1 === 'leave_types') return { tab: 'leave', sub: 'types' };
    if (seg1 === 'housekeeping') return { tab: 'leave', sub: 'housekeeping' };
    if (seg1 === 'audit') return { tab: 'security', sub: 'audit' };
    return { tab: 'directory' }; // bare #/manage or #/manage/users
  }

  // ---- current tabs, no sub ----
  if (seg0 === 'today') return { tab: 'today' };
  if (seg0 === 'board') {
    if (seg1 === 'cos') return { tab: 'board', sub: 'cos' };
    if (seg1 === 'card' && /^\d+$/.test(seg2 ?? '') && Number.isSafeInteger(Number(seg2)) && Number(seg2) > 0)
      return { tab: 'board', sub: 'card', cardId: Number(seg2), update: params.get('update') === '1' };
    return { tab: 'board', checkin: params.get('checkin') === '1' };
  }
  if (seg0 === 'team_board') return { tab: 'team_board' };
  if (seg0 === 'my_requests') return { tab: 'my_requests' };
  if (seg0 === 'history') return { tab: 'history' };
  if (seg0 === 'approvals') return { tab: 'approvals' };
  if (seg0 === 'remote') return { tab: 'remote' };
  if (seg0 === 'alerts') return { tab: 'alerts' };
  if (seg0 === 'field') return { tab: 'field' };

  // ---- current tabs, URL-addressable sub ----
  if (seg0 === 'continuity') {
    const s = VALID_CONTINUITY_SUBS.includes(seg1 as ContinuitySub) ? (seg1 as ContinuitySub) : DEFAULT_ADMIN_CONTINUITY_SUB;
    return { tab: 'continuity', sub: s };
  }
  if (seg0 === 'overview') {
    const s = VALID_OVERVIEW_SUBS.includes(seg1 as OverviewSub) ? (seg1 as OverviewSub) : DEFAULT_OVERVIEW_SUB;
    return { tab: 'overview', sub: s };
  }
  if (seg0 === 'directory') {
    if (seg1 === 'person') {
      const userId = Number(seg2);
      if (Number.isInteger(userId) && userId > 0) return { tab: 'directory', sub: 'person', userId };
    }
    return { tab: 'directory' };
  }
  if (seg0 === 'sites') {
    const s = VALID_SITES_SUBS.includes(seg1 as SitesSub) ? (seg1 as SitesSub) : DEFAULT_SITES_SUB;
    return { tab: 'sites', sub: s };
  }
  if (seg0 === 'leave') {
    const s = VALID_LEAVE_SUBS.includes(seg1 as LeaveSub) ? (seg1 as LeaveSub) : DEFAULT_LEAVE_SUB;
    return { tab: 'leave', sub: s };
  }
  if (seg0 === 'security') {
    const s = VALID_SECURITY_SUBS.includes(seg1 as SecuritySub) ? (seg1 as SecuritySub) : DEFAULT_SECURITY_SUB;
    return { tab: 'security', sub: s };
  }
  if (seg0 === 'handbook') {
    const s = VALID_HANDBOOK_SUBS.includes(seg1 as HandbookSub) ? (seg1 as HandbookSub) : DEFAULT_HANDBOOK_SUB;
    return { tab: 'handbook', sub: s };
  }

  return { tab: 'today' };
}

class RouteState {
  current = $state<Route>(parseHash(location.hash));

  push(r: Route) {
    this.current = r;
    const hash = toHash(r);
    if (location.hash !== hash) history.pushState(null, '', hash);
  }

  replace(r: Route) {
    this.current = r;
    const hash = toHash(r);
    if (location.hash !== hash) history.replaceState(null, '', hash);
  }
}

export const route = new RouteState();

// routeFromPath parses a stored route string (e.g. a notification's `route`
// field, "/hr/inbox") into a Route the same way the URL hash is parsed —
// same validation/fallback rules, so a stale or malformed stored route just
// lands on Today instead of crashing navigation.
export function routeFromPath(path: string): Route {
  return parseHash(path);
}

function syncFromLocation() {
  route.current = parseHash(location.hash);
}
window.addEventListener('popstate', syncFromLocation);
window.addEventListener('hashchange', syncFromLocation);
